import { createContext, useMemo } from 'react'
import { getEmployeeFullName } from '../../../../../util/table.util'

export const FormOptionsContext = createContext({})

/**
 * transforms the employees groups array in dropdown options based on the group type
 */
const getGroupOptions = (allEmployeesGroups, groupType) =>
  allEmployeesGroups
    .filter((item) => item.groupType === groupType)
    .map((item) => ({
      value: item.id,
      label: item.name,
    }))
    .sort((a, b) => a?.label?.localeCompare(b?.label))

/**
 * transforms the dropdown options in a map { value -> label } so selected options labels can be easily accessed later
 */
const toOptionsMap = (options = []) => {
  const result = {}
  options.forEach((option) => {
    result[option.value] = option.label
  })
  return result
}

/**
 * Provides the options for the select/multiselect components of the Onboarding modal
 * Alogside with the map of { value -> label }
 */
export const useFormOptions = ({
  allEmployees = [],
  allEmployeesGroups = [],
  customerDomains = [],
  customerLicenses = [],
  isFetchingCustomerEmployees = false,
}) => {
  const licensesMap = useMemo(() => {
    const result = {}
    customerLicenses?.forEach((item) => {
      result[item.id] = item.displayName
    })
    return result
  }, [customerLicenses])

  const employeeEmailOptions = useMemo(
    () =>
      allEmployees
        .filter((employee) => employee && !employee.ignored && employee.employeeId != null && employee.email)
        .map((employee) => ({
          label: `${getEmployeeFullName(employee)} (${employee.email})`,
          value: employee.employeeId,
        }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allEmployees],
  )
  const employeeEmailOptionsMap = useMemo(() => toOptionsMap(employeeEmailOptions), [employeeEmailOptions])

  const employeeOptions = useMemo(
    () =>
      allEmployees
        .filter((employee) => !employee.ignored)
        .map((employee) => ({ label: getEmployeeFullName(employee), value: employee.employeeId }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allEmployees],
  )

  const domainOptions = useMemo(
    () =>
      customerDomains
        ?.filter((domain) => domain.isVerified)
        ?.map((domain) => ({ label: domain.id, value: domain.id })) ?? [],
    [customerDomains],
  )
  const domainOptionsMap = useMemo(() => toOptionsMap(domainOptions), [domainOptions])

  const m365GroupOptions = useMemo(() => getGroupOptions(allEmployeesGroups, 'M365Group'), [allEmployeesGroups])
  const m365GroupOptionsMap = useMemo(() => toOptionsMap(m365GroupOptions), [m365GroupOptions])

  const securityGroupOptions = useMemo(() => getGroupOptions(allEmployeesGroups, 'SecurityGroup'), [allEmployeesGroups])

  const securityGroupOptionsMap = useMemo(() => toOptionsMap(securityGroupOptions), [securityGroupOptions])

  const distributionGroupOptions = useMemo(
    () =>
      getGroupOptions(allEmployeesGroups, 'DistributionGroup').concat(
        getGroupOptions(allEmployeesGroups, 'SecurityMailEnabledGroup'),
      ),
    [allEmployeesGroups],
  )
  const distributionGroupOptionsMap = useMemo(() => toOptionsMap(distributionGroupOptions), [distributionGroupOptions])

  const sharedMailboxGroupOptions = useMemo(
    () => getGroupOptions(allEmployeesGroups, 'SharedMailbox'),
    [allEmployeesGroups],
  )
  const sharedMailboxGroupOptionsMap = useMemo(
    () => toOptionsMap(sharedMailboxGroupOptions),
    [sharedMailboxGroupOptions],
  )

  // memoizing here because this is meant to be passed to the FormOptionsContext and other hooks
  const provided = useMemo(
    () => ({
      customerLicenses,
      licensesMap,
      employeeEmailOptions,
      employeeEmailOptionsMap,
      employeeOptions,
      isFetchingCustomerEmployees,
      domainOptions,
      domainOptionsMap,
      m365GroupOptions,
      m365GroupOptionsMap,
      securityGroupOptions,
      securityGroupOptionsMap,
      distributionGroupOptions,
      distributionGroupOptionsMap,
      sharedMailboxGroupOptions,
      sharedMailboxGroupOptionsMap,
    }),
    [
      customerLicenses,
      licensesMap,
      employeeEmailOptions,
      employeeEmailOptionsMap,
      employeeOptions,
      isFetchingCustomerEmployees,
      domainOptions,
      domainOptionsMap,
      m365GroupOptions,
      m365GroupOptionsMap,
      securityGroupOptions,
      securityGroupOptionsMap,
      distributionGroupOptions,
      distributionGroupOptionsMap,
      sharedMailboxGroupOptions,
      sharedMailboxGroupOptionsMap,
    ],
  )

  return provided
}
